import React from "react";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSIconButton from "../../system-design/DSIconButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSTextField from "../../system-design/DSTextField";
import LoadKlydosButton from "./LoadKlydosButton";
import { ChevronLeft, ChevronRight, Today, Clear } from "@mui/icons-material";
import { KlydoError } from "../../../components/hooks/types";

export enum Directions {
  PREV = "prev",
  NEXT = "next",
}
type TodayEventsNavigationProps = {
  currentDate: Date;
  onNavigate: (direction: Directions) => void;
  onGoToToday: () => void;
  onDateChange: (date: Date) => void;
  onLoadKlydos: () => void;
  klydosLoading: boolean;
  klydosError: KlydoError | null;
  klydosLoaded: boolean;
};

const TodayEventsNavigation = ({
  currentDate,
  onNavigate,
  onGoToToday,
  onDateChange,
  onLoadKlydos,
  klydosLoading,
  klydosError,
  klydosLoaded,
}: TodayEventsNavigationProps) => {
  const formattedDate = currentDate.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const isToday = () => {
    const today = new Date();
    return (
      currentDate.getDate() === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const handleDateInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    if (value) {
      const selectedDate = new Date(value);
      onDateChange(selectedDate);
    }
  };

  const handleClearDate = () => {
    onGoToToday();
  };

  const dateInputValue = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, "0")}-${currentDate.getDate().toString().padStart(2, "0")}`;

  return (
    <DSBox
      sx={{
        p: 2,
        backgroundColor: "background.paper",
        borderRadius: 1,
        boxShadow: 1,
        mb: 2,
      }}
    >
      <DSBox sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
        <DSTextField
          label="Pick Date"
          type="date"
          value={dateInputValue}
          onChange={handleDateInputChange}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ minWidth: 200 }}
        />
        <DSSecondaryButton
          onClick={handleClearDate}
          startIcon={<Clear />}
          disabled={isToday()}
        >
          Clear
        </DSSecondaryButton>
        {!isToday() && (
          <DSSecondaryButton startIcon={<Today />} onClick={onGoToToday}>
            Today
          </DSSecondaryButton>
        )}
      </DSBox>

      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <DSIconButton
          onClick={() => onNavigate(Directions.PREV)}
          sx={{ color: "primary.main" }}
        >
          <ChevronLeft />
        </DSIconButton>

        <DSBox sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <DSTypography variant="h6" sx={{ fontWeight: "bold" }}>
            {formattedDate}
          </DSTypography>
        </DSBox>

        <DSIconButton
          onClick={() => onNavigate(Directions.NEXT)}
          sx={{ color: "primary.main" }}
        >
          <ChevronRight />
        </DSIconButton>
      </DSBox>

      <DSBox sx={{ mt: 2 }}>
        <LoadKlydosButton
          onLoadKlydos={onLoadKlydos}
          klydosLoading={klydosLoading}
          klydosError={klydosError}
          klydosLoaded={klydosLoaded}
        />
      </DSBox>
    </DSBox>
  );
};

export default TodayEventsNavigation;
